import { CompartmentSelector, CompartmentView, Pagination } from '@ekkogmbh/apisdk';
import { Box, Fade, Stack, Typography } from '@mui/material';
import { Add, Delete, Edit, FindReplace } from '@mui/icons-material';
import MUIDataTable from 'mui-datatables';
import { useCallback, useMemo, useState } from 'react';
import { GenericDialog } from 'src/Common/Components/GenericDialog';
import { ColumnDef, DataTable, dataTableHighlight } from 'src/Common/Components/DataTable';
import Permissions from 'src/Common/Permissions';
import { CompartmentSelectorPanel } from './CompartmentSelectorPanel';
import React from 'react';
import { ContentControl } from 'src/Common/Components/ContentControl';
import { ContentControlButton } from 'src/Common/Components/ContentControl/ContentControlButton';
import { RunCompartmentSelectorResult } from './RunComartmentSelectorResult';
import CompartmentSelectorConfigurationField from './CompartmentSelectorConfigurationField';
import ScrollableStack from 'src/Common/Components/ScrollableStack';
import useEkko from 'src/Common/Providers/EkkoProvider';
import { useContentControl } from 'src/Common/Stores/ContentControlProvider';
import useRequest from 'src/Common/Hooks/useRequest';
import useNavigation from 'src/Common/Providers/NavigationProvider';
import usePagination from 'src/Common/Providers/PaginationProvider';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import { CenteredColumn } from 'src/Common/Components/Stacks';

const ROW_MIN_HEIGHT = 64;

export const CompartmentSelectorManagementContent = (): React.JSX.Element => {
  const { api } = useEkko();
  const { refresh } = usePagination();
  const { scrollTop } = useNavigation();
  const { isPanelOpen, setPanelOpen } = useContentControl();

  const [isLoading, startLoading] = useRequest();
  const [isResultShown, setResultShown] = useState<boolean>(false);
  const [compartments, setCompartments] = useState<CompartmentView[]>([]);
  const [openDialog, setOpenDialog] = useState<'test' | 'delete'>();
  const [selectedSelector, setSelectedSelector] = useState<CompartmentSelector>();
  const [selectorInput, setSelectorInput] = useState<string>('');

  const dismiss = () => {
    setCompartments([]);
    setResultShown(false);
    setSelectedSelector(undefined);
    setPanelOpen(false);
    setOpenDialog(undefined);
    setSelectorInput('');
  };

  const deleteSelector = useCallback(() => {
    if (selectedSelector === undefined) {
      return;
    }

    startLoading(
      async () => {
        const { coordinate, name } = selectedSelector;
        await api.deleteCompartmentSelector(coordinate, name);
      },
      () => {
        dismiss();
        refresh();
      },
    );
  }, [api.deleteCompartmentSelector, selectedSelector]);

  const saveSelector = useCallback(
    (selector: CompartmentSelector) => {
      const isOverwrite = selectedSelector !== undefined;

      startLoading(
        () => api.saveCompartmentSelector(selector, isOverwrite),
        () => {
          dismiss();
          scrollTop();
          refresh();
        },
      );
    },
    [selectedSelector],
  );

  const testSelector = useCallback(
    (value: string) => {
      if (selectedSelector === undefined) {
        return;
      }
      startLoading(
        async () => {
          const { name, coordinate } = selectedSelector;
          return await api.runCompartmentSelector(coordinate, name, value);
        },
        (comps) => {
          setCompartments(comps as CompartmentView[]);
        },
        () => {
          setCompartments([]);
          setResultShown(false);
        },
      );
    },
    [selectedSelector],
  );

  const compartmentTable = useMemo(
    () => (
      <Box sx={{ p: 2 }}>
        <MUIDataTable
          title={''}
          data={compartments.map((c) => ({
            coordinate: c.coordinate,
            links: c.links.map((l) => l.labelId).join(', '),
          }))}
          columns={[
            { label: `Coordinate`, name: 'coordinate' },
            { label: 'Linked Label Ids', name: 'links' },
          ]}
          options={{ responsive: 'standard', selectableRows: 'none' }}
        />
      </Box>
    ),
    [compartments],
  );

  const dialog = useMemo(() => {
    const hasCompartments = compartments.length > 0;

    if (selectedSelector === undefined) {
      return <></>;
    }

    switch (openDialog) {
      case 'delete':
        return (
          <GenericDialog
            open
            fullWidth
            centered
            type="confirmation"
            maxWidth={'sm'}
            title={'Delete Compartment Selector'}
            text={selectedSelector!.name}
            onClose={dismiss}
            onConfirm={deleteSelector}
          />
        );
      case 'test': {
        let content: React.JSX.Element;
        if (hasCompartments) {
          content = (
            <Stack direction={'column'} spacing={2}>
              <RunCompartmentSelectorResult
                compartments={compartments}
                showResultsHandler={() => setResultShown(true)}
              />
              {isResultShown && (
                <Fade in unmountOnExit>
                  <div>{compartmentTable}</div>
                </Fade>
              )}
            </Stack>
          );
        } else {
          content = <Typography>{selectedSelector.name}</Typography>;
        }

        return (
          <GenericDialog
            open
            fullWidth
            centered
            type="input"
            maxWidth={'sm'}
            title={'Test Compartment Selector'}
            text={
              <CenteredColumn width={'100%'}>
                {content}
                <StyledTextField
                  label={'Search for Compartments'}
                  value={selectorInput}
                  onChange={(e) => {
                    setSelectorInput(e.target.value as string);
                  }}
                  onKeyDown={(e) => {
                    switch (e.key) {
                      case 'Enter':
                        testSelector(selectorInput);
                        break;
                      case 'Escape':
                        setSelectorInput('');
                        break;
                    }
                  }}
                />
              </CenteredColumn>
            }
            onClose={dismiss}
            onSubmit={() => testSelector(selectorInput)}
            buttonText={{ cancel: 'Close', ok: 'Search' }}
            checkmarkSpinner={isLoading}
          />
        );
      }
      default:
        return undefined;
    }
  }, [
    deleteSelector,
    testSelector,
    openDialog,
    selectedSelector,
    compartments,
    compartmentTable,
    isLoading,
    selectorInput,
    isResultShown,
  ]);

  const fetchItems = useCallback((pagination: Pagination) => api.getCompartmentSelectors(pagination), [api]);

  return (
    <ContentControl
      panel={
        <CompartmentSelectorPanel
          compartmentSelector={selectedSelector}
          closeHandler={dismiss}
          saveHandler={saveSelector}
        />
      }
      dialog={dialog}
      buttons={[
        <ContentControlButton
          key={'add'}
          tooltip={'Add Compartment Selector'}
          onClick={() => {
            dismiss();
            setSelectedSelector(undefined);
            setPanelOpen(!isPanelOpen);
          }}
          content={<Add />}
          requiredPermission={Permissions.COMPARTMENT_SELECTORS_WRITE}
        />,
      ]}
    >
      <DataTable
        actions={[
          {
            title: 'Test',
            icon: <FindReplace />,
            onClick: (entry: CompartmentSelector) => {
              dismiss();
              setSelectedSelector(entry);
              setOpenDialog('test');
            },
            disabled: isLoading,
          },
          {
            title: 'Edit',
            icon: <Edit />,
            onClick: (entry: CompartmentSelector) => {
              dismiss();
              setSelectedSelector(entry);
              setPanelOpen(true);
              scrollTop();
            },
            disabled: isLoading,
          },
          {
            title: 'Delete',
            icon: <Delete />,
            onClick: (entry: CompartmentSelector) => {
              dismiss();
              setSelectedSelector(entry);
              setOpenDialog('delete');
            },
            disabled: isLoading,
          },
        ]}
        columns={
          [
            {
              header: 'Name',
              body: (entry: CompartmentSelector, searchText?: string) => (
                <ScrollableStack direction={'row'} width={'100%'} minHeight={ROW_MIN_HEIGHT} alignItems={'center'}>
                  {searchText ? dataTableHighlight(entry.name, [searchText]) : entry.name}
                </ScrollableStack>
              ),
              filterField: 'name',
              sortField: 'CS.name',
              proportionalContent: '25%',
            },
            {
              header: 'Coordinate',
              body: (entry: CompartmentSelector, searchText?: string) => (
                <ScrollableStack direction={'row'} width={'100%'} minHeight={ROW_MIN_HEIGHT} alignItems={'center'}>
                  {searchText ? dataTableHighlight(entry.coordinate, [searchText]) : entry.coordinate}
                </ScrollableStack>
              ),
              filterField: 'coordinate',
              sortField: 'N.materializedPath',
              proportionalContent: '25%',
            },
            {
              header: 'Type',
              body: (entry: CompartmentSelector) => entry.type,
              fitContent: true,
            },
            {
              header: 'Configuration',
              body: (entry: CompartmentSelector) => (
                <ScrollableStack direction={'row'} width={'100%'} minHeight={ROW_MIN_HEIGHT}>
                  <CompartmentSelectorConfigurationField configuration={entry.configuration} />
                </ScrollableStack>
              ),
              proportionalContent: '50%',
            },
          ] as ColumnDef[]
        }
        fetchItems={fetchItems}
      />
    </ContentControl>
  );
};
